/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import React, { useState, useMemo } from 'react';

export interface IReadMoreTextProps {
    /**
     * The HTML content to display
     */
    htmlContent: string;
    /**
     * Character limit after which Read More/Read Less will be enabled
     */
    characterLimit: number;
    /**
     * CSS class name for the container
     */
    className?: string;
    /**
     * Text for "Read More" link
     */
    readMoreText?: string;
    /**
     * Text for "Read Less" link
     */
    readLessText?: string;
}

/**
 * Strips HTML tags from a string to get plain text content
 * @param html - HTML string to strip tags from
 * @returns Plain text content
 */
const stripHtmlTags = (html: string): string => {
    if (!html) {
        return '';
    }
    // Create a temporary div element to parse HTML
    if (typeof document !== 'undefined') {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || '';
    }
    // Fallback for SSR - use regex to strip tags
    return html.replace(/<[^>]*>/g, '');
};

/**
 * Truncates HTML content to a specified character limit while preserving HTML structure
 * @param html - HTML string to truncate
 * @param limit - Character limit (based on plain text)
 * @returns Truncated HTML string
 */
const truncateHtmlContent = (html: string, limit: number): string => {
    if (!html || limit <= 0) {
        return '';
    }

    let charCount = 0;
    let result = '';
    let tagStack: string[] = [];
    let i = 0;

    while (i < html.length && charCount < limit) {
        if (html[i] === '<') {
            // Find the end of the tag
            const tagEnd = html.indexOf('>', i);
            if (tagEnd === -1) {
                break;
            }

            const tag = html.substring(i, tagEnd + 1);
            result += tag;

            // Check if it's an opening, closing, or self-closing tag
            const tagMatch = tag.match(/<\/?([a-zA-Z0-9]+)(\s|>|\/)/);
            if (tagMatch) {
                const tagName = tagMatch[1].toLowerCase();
                const isClosingTag = tag.startsWith('</');
                const isSelfClosing = tag.endsWith('/>') || ['br', 'hr', 'img', 'input', 'meta', 'link'].includes(tagName);

                if (!isSelfClosing) {
                    if (isClosingTag) {
                        // Pop from stack
                        const lastIndex = tagStack.lastIndexOf(tagName);
                        if (lastIndex !== -1) {
                            tagStack.splice(lastIndex, 1);
                        }
                    } else {
                        // Push to stack
                        tagStack.push(tagName);
                    }
                }
            }

            i = tagEnd + 1;
        } else if (html[i] === '&') {
            // Handle HTML entities
            const entityEnd = html.indexOf(';', i);
            if (entityEnd !== -1 && entityEnd - i < 10) {
                result += html.substring(i, entityEnd + 1);
                i = entityEnd + 1;
            } else {
                result += html[i];
                i++;
            }
            charCount++;
        } else {
            result += html[i];
            charCount++;
            i++;
        }
    }

    // Add ellipsis if truncated
    if (charCount >= limit && i < html.length) {
        result += '...';
    }

    // Close any open tags
    while (tagStack.length > 0) {
        const tagToClose = tagStack.pop();
        result += `</${tagToClose}>`;
    }

    return result;
};

/**
 * ReadMoreText component that displays HTML content with Read More/Read Less functionality
 */
const ReadMoreText: React.FC<IReadMoreTextProps> = ({
    htmlContent,
    characterLimit,
    className = 'ms-content-block__text',
    readMoreText = 'Read More',
    readLessText = 'Read Less'
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Calculate if we need to show Read More/Read Less based on plain text length
    const { shouldTruncate, truncatedContent } = useMemo(() => {
        const plainText = stripHtmlTags(htmlContent);
        const shouldTruncate = characterLimit > 0 && plainText.length > characterLimit;
        const truncatedContent = shouldTruncate ? truncateHtmlContent(htmlContent, characterLimit) : htmlContent;

        return {
            shouldTruncate,
            truncatedContent,
            plainTextLength: plainText.length
        };
    }, [htmlContent, characterLimit]);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    // If no truncation needed, render content as-is
    if (!shouldTruncate) {
        return <div className={className} dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }

    return (
        <div className={`${className} ms-content-block__text-read-more-container`}>
            <div
                className='ms-content-block__text-content'
                dangerouslySetInnerHTML={{ __html: isExpanded ? htmlContent : truncatedContent }}
            />
            <button type='button' className='ms-content-block__read-more-button' onClick={handleToggle} aria-expanded={isExpanded}>
                {isExpanded ? readLessText : readMoreText}
            </button>
        </div>
    );
};

export default ReadMoreText;
